import { useCallback, useEffect, useState } from "react"

// A 404 means the thing doesn't exist; anything else is a blip the caller may
// want to retry rather than treat as a verdict.
export type FetchFailure = "not-found" | "error" | null

// A null path skips the request, for callers that don't have one to make yet.
export default function useFetch<T>(path: string | null): [T | undefined, boolean, FetchFailure] {
  const [response, setResponse] = useState<T | undefined>(undefined)
  const [loading, setLoading] = useState(true)
  const [failure, setFailure] = useState<FetchFailure>(null)

  const refresh = useCallback(() => {
    if (!path) {
      setLoading(false)
      return
    }

    setLoading(true)
    setFailure(null)

    fetch(`${process.env.REACT_APP_API_URL}${path}`)
      .then((res) => {
        if (res.status === 404) {
          throw new Error("not-found")
        }
        if (!res.ok) {
          throw new Error("Request failed")
        }
        return res.json()
      })
      .then((res) => {
        setResponse(res)
        setLoading(false)
      })
      .catch((err: Error) => {
        setFailure(err.message === "not-found" ? "not-found" : "error")
        setLoading(false)
      })
  }, [path])

  useEffect(() => {
    refresh()
  }, [refresh])

  return [response as T | undefined, loading, failure]
}
